import React from 'react';
import { useTopProductsQuery } from '@/api';
import type { TopProducts } from '@/api/models';
import type { ErrorResponse } from '@/api/handlers/responses';
import { useSales } from '@/pages/sales/SalesProvider';

/**
 * Returns a `TopProducts` snapshot for the widget, scoped to the
 * page-level date range published through `SalesProvider`. Changing
 * the period filter on the page re-runs the underlying query because
 * `useTopProductsQuery` keys on `from` / `to`.
 *
 * Mapping rules mirror the other Sales hooks:
 *
 *   - `isLoading` → `query.isPending` (true only on the first fetch).
 *   - `data` → the deserialized `TopProducts` on success, `null` otherwise.
 *   - `error` → the `ErrorResponse` from the unsuccessful `ApiResponse`, or
 *     `null` on success. The queryFn never rejects (errors are folded into
 *     the response envelope by `makeApiRequest`).
 *   - `onRetry` → `query.refetch` adapted to a `() => void`.
 */
export const useTopProducts = () => {
    const { dateRange } = useSales();
    const query = useTopProductsQuery({
        from: dateRange.start,
        to: dateRange.end,
    });
    const { refetch } = query;

    const data = React.useMemo<TopProducts | null>(
        () => (query.data?.success ? query.data.response : null),
        [query.data],
    );

    const error = React.useMemo<ErrorResponse | null>(
        () =>
            query.data && !query.data.success
                ? (query.data.error ?? null)
                : null,
        [query.data],
    );

    const onRetry = React.useCallback(() => {
        void refetch();
    }, [refetch]);

    return {
        data,
        error,
        isLoading: query.isPending,
        onRetry,
    } as const;
};
